import React from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button, Grid, TextField } from "@mui/material";

import AppForm from "components/Shared/AppForm";
import BaseSelect from "components/Shared/Base/BaseSelect";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";

import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLVehicleListQuery } from "store/services/vehicleApi";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLDriverListQuery } from "store/services/driverApi";
import { useAuth } from "hooks";
import { CONTRACTOR_TYPES } from "data/static/staticOptions";
import { CustomCountryPhoneInput } from "components/Shared/CustomCountryPhoneInput";

const INITIAL_STATE = {
  name: "",
  category: "",
  email: "",
  phone: "",
  isActive: true,
  terms_and_conditions: "",
  vehicle_id: [],
  account: [],
  entity: [],
  driverId: [],
};

const ContractorForm = ({
  isBusy = false,
  contractorDetail = null,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity || null,
      account: viewEntity?.entity?.account || null,
    },
  });

  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });
  const _watchDriver = useWatch({ control, name: "driverId" });
  const selectedVehicle = (_watchDriver || []).flatMap(
    (driver) => driver?.assigned_vehicle_ids || []
  );

  const { data: accounts = [], isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();

  const [fetchEntities, { data: entities = [], isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();

  const [fetchVehicles] = useLazyGetDDLVehicleListQuery();

  const [fetchDrivers, { data: drivers = [], isLoading: loadingDrivers }] =
    useLazyGetDDLDriverListQuery();

  const handleOnContractorSubmit = (data) => {
    onSubmit({
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      vehicle_id: data?.vehicle_id?.map((v) => v._id) || [],
      driverId: data?.driverId?.map((d) => d._id) || [],
      isActive: data?.isActive !== undefined ? data.isActive : true,
    });
  };

  React.useEffect(() => {
    if (contractorDetail) {
      reset({
        ...contractorDetail,
        driverId: Array.isArray(contractorDetail?.driverId)
          ? contractorDetail.driverId
          : [],
      });
    }
  }, [contractorDetail, reset]);

  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
      });
    }
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity,
  ]);

  React.useEffect(() => {
    if (_watchEntity?._id) {
      fetchVehicles({
        account: _watchAccount?._id,
        entity: _watchEntity?._id,
      });
      fetchDrivers({
        account: _watchAccount?._id,
        entity: _watchEntity?._id,
      });
    }
  }, [_watchAccount?._id, _watchEntity?._id, fetchVehicles, fetchDrivers]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && !loadingAccounts && accounts && (
            <Grid item xs={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value || null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", null);
                    }}
                    options={accounts}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}

          {(isAccountAdmin || isSystemAdmin) && !loadingEntities && entities && (
            <Grid item xs={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    required
                    label="Entity"
                    value={field.value || null}
                    onChange={(_, data) => field.onChange(data)}
                    options={entities}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          {/* Name Field */}
          <Grid item xs={6}>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Please provide a name." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Name"
                  error={!!errors.name}
                  helperText={errors?.name?.message}
                />
              )}
            />
          </Grid>

          {/* Category */}
          <Grid item xs={6}>
            <Controller
              name="category"
              control={control}
              rules={{ required: "Enter category" }}
              render={({ field, fieldState: { error } }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  label="Category"
                  options={CONTRACTOR_TYPES}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          {/* Email Field */}
          <Grid item xs={6}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: "Enter your email.",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: "Invalid email address",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Email"
                  type="email"
                  error={!!errors.email}
                  helperText={errors?.email?.message}
                />
              )}
            />
          </Grid>

          {/* Phone Field */}
           <Grid item md={6}>
            <Controller
             name="phone"
              control={control}
              rules={{
               required: "Phone is required",
                validate: (value) => {
                  const digits = value.replace(/\D/g, "");
                  if (digits.length < 7 || digits.length > 17) {
                    return "Phone number must be between 9 and 16 digits";
                  }
                  return true;
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <CustomCountryPhoneInput
                  placeholder="Phone Number"
                  label="Phone*"
                  {...field}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          {/* Driver ID Field */}
          <Grid item xs={6}>
            <Controller
              name="driverId"
              control={control}
              rules={{ required: "Please select Driver ID." }}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  multiple
                  label="Driver ID"
                  required
                  value={Array.isArray(field.value) ? field.value : []}
                  onChange={(_, data) => {
                    field.onChange(data);
                    setValue("vehicle_id", []);
                  }}
                  options={drivers}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  loading={loadingDrivers}
                  loadingText="Loading Drivers..."
                  helperText={errors?.driverId?.message}
                  error={!!errors?.driverId}
                  filterSelectedOptions
                />
              )}
            />
          </Grid>

          {/* Vehicle IDs Field */}
          <Grid item xs={6}>
            <Controller
              name="vehicle_id"
              control={control}
              rules={{ required: "Please select Vehicle ID." }}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  multiple
                  label="Vehicle IDs"
                  value={Array.isArray(field.value) ? field.value : []}
                  onChange={(_, data) => field.onChange(data)}
                  getOptionLabel={(option) =>
                    option?.vehicle_id && option?.registration_number
                      ? `${option.vehicle_id} - ${option.registration_number}`
                      : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?.vehicle_id === value?.vehicle_id
                  }
                  options={selectedVehicle}
                  disabled={!_watchDriver?.length}
                  loadingText="Loading Vehicles..."
                  required
                  helperText={errors?.vehicle_id?.message}
                  error={!!errors?.vehicle_id}
                  filterSelectedOptions
                />
              )}
            />
          </Grid>

          {/* Terms and Conditions */}
          <Grid item xs={12}>
            <Controller
              name="terms_and_conditions"
              control={control}
              rules={{ required: "Terms and conditions are required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Terms and Conditions"
                  multiline
                  rows={4}
                  error={!!errors.terms_and_conditions}
                  helperText={errors?.terms_and_conditions?.message}
                />
              )}
            />
          </Grid>

          {contractorDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnContractorSubmit)}
        >
          {contractorDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default ContractorForm;